import React from 'react';
import { motion } from 'framer-motion';
import {
  FolderOpen, Inbox, ShieldCheck, Users, Share2, BarChart3,
  Clock, Slack, Mail, Calendar, CheckSquare,
} from 'lucide-react';

/** A folder-shaped card that opens slightly on hover. */
function FolderCard({
  index,
  title,
  oneLiner,
  bullets,
  Icon,
  tabColor,
  bodyTint,
}: {
  index: number;
  title: string;
  oneLiner: string;
  bullets: string[];
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  tabColor: string;
  bodyTint: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24, rotate: -1 }}
      whileInView={{ opacity: 1, y: 0, rotate: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: 'easeOut' }}
      whileHover={{ y: -4 }}
      className="relative group"
    >
      {/* Folder tab (back layer) */}
      <div
        className="absolute -top-3 left-6 w-32 h-6 rounded-t-lg shadow-sm"
        style={{ backgroundColor: tabColor }}
      />

      {/* Folder body */}
      <div
        className="relative rounded-2xl p-6 border border-gray-200/70 shadow-sm transition-shadow group-hover:shadow-xl"
        style={{ backgroundColor: bodyTint }}
      >
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm"
            style={{ backgroundColor: tabColor }}
          >
            <Icon size={20} className="text-white" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold">
            0{index + 1}
          </span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4 min-h-[40px]">{oneLiner}</p>
        <div className="flex flex-wrap gap-1.5 pt-3 border-t border-gray-200/70">
          {bullets.map(b => (
            <span
              key={b}
              className="text-[11px] px-2 py-0.5 rounded-md bg-white/70 border border-gray-200 text-gray-600"
            >
              {b}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

const FOLDERS = [
  {
    title: 'Media Library',
    oneLiner: 'Upload, organize, and search across thousands of images and videos.',
    bullets: ['Folders', 'Tags', 'Smart Collections', 'Versioning'],
    Icon: FolderOpen,
    tabColor: '#3B82F6',
    bodyTint: '#EFF6FF',
  },
  {
    title: 'File Requests',
    oneLiner: 'Buyers brief, editors deliver — one thread per request.',
    bullets: ['Public upload links', 'Multi-editor', 'Deadlines'],
    Icon: Inbox,
    tabColor: '#F97316',
    bodyTint: '#FFF7ED',
  },
  {
    title: 'Quality Check',
    oneLiner: 'Two-phase review queue — pass, fail, or reassign with notes.',
    bullets: ['Phase 1', 'Phase 2', 'Re-review trail'],
    Icon: ShieldCheck,
    tabColor: '#10B981',
    bodyTint: '#ECFDF5',
  },
  {
    title: 'Roles & Verticals',
    oneLiner: 'Built for editors, buyers, team leads, vertical heads, and admins.',
    bullets: ['RBAC', 'Multi-role users', 'Vertical-scoped views'],
    Icon: Users,
    tabColor: '#8B5CF6',
    bodyTint: '#F5F3FF',
  },
  {
    title: 'Sharing & Delivery',
    oneLiner: 'Bulk ZIPs, public links, per-buyer download codes, Slack sync.',
    bullets: ['200-file ZIPs', 'Progress widget', 'Code-gated downloads'],
    Icon: Share2,
    tabColor: '#EC4899',
    bodyTint: '#FDF2F8',
  },
  {
    title: 'Analytics & Audit',
    oneLiner: 'Facebook + RedTrack metrics on every creative, full audit trail.',
    bullets: ['Activity logs', 'Tracking reports', 'Workload dashboard'],
    Icon: BarChart3,
    tabColor: '#475569',
    bodyTint: '#F1F5F9',
  },
];

const CAPABILITIES = [
  'Script Tracker', 'Script Database', 'Launch Requests', 'Access Requests',
  'Smart Collections', 'Comments', 'Starred', 'Recents', 'Trash',
  'Public Sharing', 'Chrome Extension', 'Slack Integration',
  'Metadata Extraction', 'EXIF / IPTC tools', 'Bulk Metadata Operations',
  'Move / Copy', 'File-level Activity', 'Buyer Download Codes',
  'Campaign Lookup', 'FAQ Center', 'Auto-close Requests', 'Deadline Reminders',
];

export function HomeFeatures() {
  return (
    <section id="home-features" className="relative py-24 md:py-32 bg-[#f8f9fa] overflow-hidden">
      {/* Decorative corners — re-themed for creative library */}
      <DecorativeCorners />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-gray-400 font-semibold mb-4">
            Everything your team needs
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight">
            Six folders. <span className="text-gray-400">One creative pipeline.</span>
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Everything from briefing to launch lives behind these six folders.
          </p>
        </motion.div>

        {/* 6 folders */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 max-w-6xl mx-auto">
          {FOLDERS.map((f, i) => (
            <FolderCard key={f.title} index={i} {...f} />
          ))}
        </div>

        {/* Capability ribbon */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-20 max-w-4xl mx-auto text-center"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-gray-400 font-semibold mb-5">
            And another 22 capabilities along the way
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {CAPABILITIES.map(c => (
              <span
                key={c}
                className="text-xs px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-700 hover:border-orange-400 hover:text-orange-600 transition-colors"
              >
                {c}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/**
 * Decorative items in the four corners — re-themed from the reference's
 * "draft/build/ship" notes to creative-library context (deadline card,
 * QC queue, integrations) so they reinforce the product story.
 */
function DecorativeCorners() {
  return (
    <>
      {/* Top-left: sticky note */}
      <motion.div
        initial={{ opacity: 0, rotate: -8, y: -10 }}
        whileInView={{ opacity: 1, rotate: -4, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        className="hidden lg:block absolute top-12 left-8 w-56 z-0"
      >
        <div className="bg-yellow-200 p-5 shadow-lg" style={{ fontFamily: '"Caveat", "Comic Sans MS", cursive' }}>
          <p className="text-gray-800 text-base leading-snug">
            Capture every brief, every revision, every approved cut — without losing the thread.
          </p>
          <div className="mt-3 w-7 h-7 bg-blue-500 rounded-md flex items-center justify-center shadow-inner">
            <CheckSquare size={14} className="text-white" />
          </div>
        </div>
      </motion.div>

      {/* Top-right: deadline card */}
      <motion.div
        initial={{ opacity: 0, x: 16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="hidden lg:block absolute top-12 right-8 w-64 z-0"
      >
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
              <Clock size={12} className="text-gray-500" />
            </div>
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Deadlines
            </span>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-semibold text-gray-900">File Request · S9 / Bizop</div>
            <div className="text-xs text-gray-500 mt-1">Time</div>
            <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md font-mono">
              <Clock size={10} /> due in 4h 12m
            </div>
          </div>
        </div>
      </motion.div>

      {/* Bottom-left: active sprints / editors */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="hidden lg:block absolute bottom-12 left-8 w-72 z-0"
      >
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Active Sprints
            </span>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-orange-500 text-white flex items-center justify-center text-xs font-bold">
                8
              </div>
              <div className="flex-1">
                <div className="text-xs font-medium text-gray-700">Editors active</div>
                <div className="h-1.5 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
                  <div className="h-full w-3/4 bg-blue-500 rounded-full" />
                </div>
              </div>
              <span className="text-[10px] font-mono text-gray-500">6/8</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-green-500 text-white flex items-center justify-center text-xs font-bold">
                3
              </div>
              <div className="flex-1">
                <div className="text-xs font-medium text-gray-700">QC queue</div>
                <div className="h-1.5 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
                  <div className="h-full w-2/3 bg-green-500 rounded-full" />
                </div>
              </div>
              <span className="text-[10px] font-mono text-gray-500">3 pending</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Bottom-right: integrations sync card */}
      <motion.div
        initial={{ opacity: 0, x: 16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="hidden lg:block absolute bottom-12 right-8 w-64 z-0"
      >
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
            Seamless Sync
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
              <Mail size={18} className="text-red-500" />
            </div>
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
              <Slack size={18} className="text-purple-600" />
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Calendar size={18} className="text-blue-600" />
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}
